import {
  OrderUI,
  OrderLine,
  OrderResponse,
  OrderLineResponse,
  CreateOrderRequest,
  CreateChildOrderRequest,
  DuplicateOrderRequest,
  UpdateOrderRequest,
  OrderFilters,
  OrderListResponse,
  OrderDetailResponse,
  OrderStatsResponse,
  OrderStatus,
  OrderSource,
  OrderTag,
  StatusHistory,
  ExportOrderOptions,
} from '@/types/order';

import { api } from '@/api/axios';

// Transform backend OrderLineResponse to UI-friendly OrderLine
const transformOrderLineToUI = (orderLine: OrderLineResponse): OrderLine => {
  return {
    id: orderLine.id,
    code: orderLine.code,
    orderId: orderLine.order_id,
    customerId: orderLine.customer_id,
    serviceId: orderLine.service_id,
    serviceName: orderLine.service?.name || '', // Populated from service data
    serviceCode: orderLine.service?.code || '', // Populated from service data
    unit: orderLine.serviceUnit || orderLine.service?.unit || '',
    quantity: orderLine.quantity,
    unitPrice: orderLine.unit_price,
    priceSource: orderLine.price_source,
    discountPercentage: orderLine.discount_percentage,
    discount: orderLine.discount,
    sellingPrice: orderLine.selling_price,
    totalPrice: orderLine.total_price,
    inventoryStatus: orderLine.inventory_status,
    currentStatus: {
      status: orderLine.current_status.status as OrderStatus,
      note: orderLine.current_status.note,
      createdBy: orderLine.current_status.createdBy,
      createdAt: orderLine.current_status.createdAt,
      delivered_quantity: orderLine.current_status.delivered_quantity,
      total_quantity: orderLine.current_status.total_quantity,
    },
    statusHistory: orderLine.status_history.map(status => ({
      status: status.status as OrderStatus,
      note: status.note,
      createdBy: status.createdBy,
      createdAt: status.createdAt,
      delivered_quantity: status.delivered_quantity,
      total_quantity: status.total_quantity,
    })),
    currentTag: orderLine.current_tag ? {
      tag: orderLine.current_tag.tag,
      note: orderLine.current_tag.note,
      createdAt: orderLine.current_tag.createdAt,
      createdBy: orderLine.current_tag.createdBy,
    } : undefined,
    tagHistory: orderLine.tag_history?.map(tag => ({
      tag: tag.tag,
      note: tag.note,
      createdAt: tag.createdAt,
      createdBy: tag.createdBy,
    })) || [],
    notes: orderLine.note,
    createdBy: orderLine.createdBy,
    createdAt: orderLine.createdAt,
    updatedAt: orderLine.updatedAt,
    // Rework tracking fields
    actual_quantity: orderLine.actual_quantity,
    error_quantity: orderLine.error_quantity,
    type: orderLine.type,
    parent_order_line_id: orderLine.parent_order_line_id,
    child_order_line_ids: orderLine.child_order_line_ids,
    rework_generation: orderLine.rework_generation,
    error_from_step: orderLine.error_from_step,
    exclude_from_invoice: orderLine.exclude_from_invoice,
  };
};

// Transform backend OrderResponse to UI-friendly OrderUI
const transformOrderToUI = (orderResponse: OrderResponse): OrderUI => {
  return {
    id: orderResponse.id,
    code: orderResponse.code,
    invoiceId: orderResponse.invoice_id, // Map invoice_id for conditional rendering
    orderParentId: orderResponse.order_parent_id, // Parent order code
    orderChildIds: orderResponse.order_child_ids, // Child order codes
    childCount: orderResponse.child_count, // Child order count
    childOrders: orderResponse.child_orders?.map(childOrder => transformOrderToUI(childOrder)), // Recursively transform child orders
    parentOrder: orderResponse.parent_order, // Parent order summary
    customerId: orderResponse.customer_id,
    customerName: orderResponse.customer?.name || '', // Use customer data from API
    customerPhone: orderResponse.customer?.phone || '', // Use customer data from API
    customerEmail: orderResponse.customer?.email || '', // Use customer data from API
    orderSource: orderResponse.order_source as OrderSource,
    status: orderResponse.current_status.status as OrderStatus,
    tag: orderResponse.tag as OrderTag,
    notes: orderResponse.note,
    orderLines: orderResponse.order_lines?.map(transformOrderLineToUI) || [],
    totalPrice: orderResponse.total_price,
    shippingAddress: orderResponse.shipping_address,
    shippingAt: orderResponse.shipping_at,
    deliveryAt: orderResponse.delivery_at,
    createdBy: orderResponse.createdBy,
    createdByName: orderResponse.created_by_name, // Map creator name from API
    createdAt: orderResponse.createdAt,
    updatedAt: orderResponse.updatedAt,
    statusHistory: orderResponse.status_history.map(status => ({
      status: status.status as OrderStatus,
      note: status.note,
      createdBy: status.createdBy,
      createdAt: status.createdAt,
      delivered_quantity: status.delivered_quantity,
      total_quantity: status.total_quantity,
    })),
    // Add customer object for printing purposes (not in OrderUI type but available at runtime)
    customer: orderResponse.customer,
  } as OrderUI;
};

// API Functions
export const orderApi = {
  // Get all orders with filtering and pagination
  getOrders: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: OrderFilters
  ): Promise<OrderListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    // Add filters to params
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await api.get(`/orders?${params.toString()}`);

    const ordersData = response.data.data || [];
    return {
      data: ordersData.map((order: OrderResponse) => transformOrderToUI(order)),
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.page_size,
    };
  },

  // Get order by code with details
  getOrderByCode: async (code: string): Promise<OrderDetailResponse> => {
    const response = await api.get(`/orders/${code}`);

        const transformedOrder = transformOrderToUI(response.data.data);
    return {
      order: transformedOrder,
      customer: response.data.data.customer || {},
      orderLines: transformedOrder.orderLines, // Use the transformed order lines
      invoice: response.data.data.invoice,
      statusHistory: response.data.data.status_history || [],
    };
  },

  // Get order by ID with details (deprecated - use getOrderByCode instead)
  getOrderById: async (id: string): Promise<OrderDetailResponse> => {
    // For backward compatibility, assume id is actually a code
    return orderApi.getOrderByCode(id);
  },

  // Create new order
  createOrder: async (orderData: CreateOrderRequest): Promise<OrderUI> => {
    const response = await api.post('/orders', orderData);
    return transformOrderToUI(response.data.data);
  },

  // Update order by code
  updateOrderByCode: async (code: string, orderData: UpdateOrderRequest): Promise<OrderUI> => {
    const response = await api.put(`/orders/${code}`, orderData);
    return transformOrderToUI(response.data.data);
  },

  // Update order (deprecated - use updateOrderByCode instead)
  updateOrder: async (id: string, orderData: UpdateOrderRequest): Promise<OrderUI> => {
    return orderApi.updateOrderByCode(id, orderData);
  },

  // Update order status by code
  updateOrderStatusByCode: async (code: string, status: OrderStatus, notes?: string): Promise<OrderUI> => {
    const statusData: StatusHistory = {
      status,
      note: notes,
      createdBy: 'current-user',
      createdAt: new Date().toISOString(),
    };

    const response = await api.put(`/orders/${code}/status`, statusData);
    return transformOrderToUI(response.data.data);
  },

  // Update order status (deprecated - use updateOrderStatusByCode instead)
  updateOrderStatus: async (id: string, status: OrderStatus, notes?: string): Promise<OrderUI> => {
    return orderApi.updateOrderStatusByCode(id, status, notes);
  },

  // Confirm production by code (Counter Manager specific action)
  confirmProductionByCode: async (code: string, notes?: string): Promise<OrderUI> => {
    const confirmData = {
      note: notes || 'Đã xác nhận sản xuất bởi Trưởng quầy',
    };

    const response = await api.put(`/orders/${code}/confirm`, confirmData);
    return transformOrderToUI(response.data.data);
  },

  // Confirm production (deprecated - use confirmProductionByCode instead)
  confirmProduction: async (id: string, notes?: string): Promise<OrderUI> => {
    return orderApi.confirmProductionByCode(id, notes);
  },

  // Update order tag by code
  updateOrderTagByCode: async (code: string, tag: string): Promise<OrderUI> => {
    const response = await api.put(`/orders/${code}/tag`, { tag });
    return transformOrderToUI(response.data.data);
  },

  // Cancel order by code
  cancelOrderByCode: async (code: string, note?: string): Promise<OrderUI> => {
    const response = await api.put(`/orders/${code}/cancel`, { note });
    return transformOrderToUI(response.data.data);
  },

  // Cancel order (deprecated - use cancelOrderByCode instead)
  cancelOrder: async (id: string, note?: string): Promise<OrderUI> => {
    return orderApi.cancelOrderByCode(id, note);
  },

  // Delete order by code
  deleteOrderByCode: async (code: string): Promise<void> => {
    await api.delete(`/orders/${code}`);
  },

  // Delete order (deprecated - use deleteOrderByCode instead)
  deleteOrder: async (id: string): Promise<void> => {
    return orderApi.deleteOrderByCode(id);
  },

  // Get order statistics
  getOrderStats: async (): Promise<OrderStatsResponse> => {
    const response = await api.get('/orders/stats');
    return response.data.data;
  },

  // Get order status counts with optional filtering
  getStatusCounts: async (params?: { parent_only?: boolean; child_only?: boolean }): Promise<Record<string, number>> => {
    const response = await api.get<{
      data: Record<string, number>;
      message: string;
      timestamp: string;
    }>('/orders/status-counts', { params });

    return response.data.data;
  },

  // Get order status history
  getOrderStatusHistory: async (orderId: string): Promise<StatusHistory[]> => {
    const response = await api.get(`/orders/${orderId}/status-history`);
    return response.data.data;
  },

  // Export orders
  exportOrders: async (options: ExportOrderOptions): Promise<Blob> => {
    const params = new URLSearchParams();

    // Add export options to params
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/orders/export?${params.toString()}`, {
      responseType: 'blob',
    });

    return response.data;
  },

  // Get pricing for order line (customer-specific pricing)
  getOrderLinePricing: async (customerId: string, serviceId: string): Promise<{
    basePrice: number;
    customerPrice?: number;
    groupPrice?: number;
    finalPrice: number;
  }> => {
    const response = await api.get('/orders/pricing', {
      params: {
        customer_id: customerId,
        service_id: serviceId,
      },
    });
    return response.data.data;
  },

  // Check if order can be edited by code
  canEditOrderByCode: async (orderCode: string): Promise<{
    canEdit: boolean;
    reason?: string;
  }> => {
    const response = await api.get(`/orders/${orderCode}/can-edit`);
    return response.data.data;
  },

  // Check if order can be edited (deprecated - use canEditOrderByCode instead)
  canEditOrder: async (orderId: string): Promise<{
    canEdit: boolean;
    reason?: string;
  }> => {
    return orderApi.canEditOrderByCode(orderId);
  },

  // Create a child order linked to a parent order
  createChildOrder: async (parentCode: string, childOrderData: CreateChildOrderRequest): Promise<OrderUI> => {
    const response = await api.post(`/orders/${parentCode}/children`, childOrderData);
    return transformOrderToUI(response.data.data);
  },

  // Get all child orders for a parent order
  getChildOrders: async (parentCode: string): Promise<OrderUI[]> => {
    const response = await api.get(`/orders/${parentCode}/children`);
    const childOrders = response.data.data || [];
    return childOrders.map((order: OrderResponse) => transformOrderToUI(order));
  },

  // Duplicate an existing order
  duplicateOrder: async (orderCode: string, duplicateData?: DuplicateOrderRequest): Promise<OrderUI> => {
    const response = await api.post(`/orders/${orderCode}/duplicate`, duplicateData || {});
    return transformOrderToUI(response.data.data);
  },
};
